import React, { useEffect, useRef, useState } from 'react';
import { Box, Loader, Paper, ScrollArea, Text, TextInput, type TextInputProps } from '@mantine/core';
import debounce from 'lodash.debounce';
import { usePostHog } from 'posthog-js/react';
import { useGoogleMapsApi } from '../../hooks/useGoogleMapsApi';
import { trackEvent } from '../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../utils/monitoring/Monitoring_Events_Enum';
import { importGoogleMapsLibrary } from '../../utils/googleMapsLoader';
import type {
  GoogleAddressComponent,
  GoogleAutocompleteSessionToken,
  GooglePlacePrediction,
  GooglePlacesLibrary,
} from '../../types/googleMaps';

const latinAmericaCountries = [
  'mx',
  'co',
  // 'ar', // Argentina
  'cl',
  'pe',
  've',
  'ec',
  'gt',
  'bo',
  'do',
  // 'hn', // Honduras
  'py',
  'sv',
  'ni',
  'uy',
  'pa',
  'cr',
];

export interface PlaceData {
  address: string;
  lat: number | null;
  lng: number | null;
  placeId?: string;
  street?: string;
  neighborhood?: string;
  postalCode?: string;
  city?: string;
  state?: string;
}

export interface AddressAutocompleteProps extends Omit<TextInputProps, 'onChange' | 'value'> {
  onPlaceSelected: (placeData: PlaceData) => void;
  value?: string;
  onChange?: (val: string) => void;
  countryRestrictions?: string[];
}

function getAddressComponent(
  components: GoogleAddressComponent[] | undefined,
  type: string,
): string | undefined {
  const component = components?.find((item) => item.types.includes(type));
  return component?.longText || component?.long_name || undefined;
}

const AddressAutocomplete = ({
  onPlaceSelected,
  label,
  placeholder = 'Ingresa la dirección',
  value = '',
  onChange,
  countryRestrictions = latinAmericaCountries,
  ...textInputProps
}: AddressAutocompleteProps) => {
  const posthog = usePostHog();
  const [inputValue, setInputValue] = useState(value);
  const [debouncedInputValue, setDebouncedInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<GooglePlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const placesLibraryRef = useRef<GooglePlacesLibrary | null>(null);
  const sessionTokenRef = useRef<GoogleAutocompleteSessionToken | null>(null);
  const { isLoaded } = useGoogleMapsApi(true);

  const debounceInput = useRef(debounce((val: string) => setDebouncedInputValue(val), 400)).current;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget.value;
    setInputValue(nextValue);
    debounceInput(nextValue);
    onChange?.(nextValue);
    setShowSuggestions(true);
  };

  useEffect(() => {
    if (!isLoaded || debouncedInputValue.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSuggestions = async () => {
      setLoading(true);

      try {
        const placesLibrary =
          placesLibraryRef.current ||
          (await importGoogleMapsLibrary<GooglePlacesLibrary>('places'));
        placesLibraryRef.current = placesLibrary;

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
        }

        const includedRegionCodes = Array.from(
          new Set(
            countryRestrictions
              .map((country) => country.trim().toUpperCase())
              .filter(Boolean),
          ),
        );

        const { suggestions: nextSuggestions } =
          await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: debouncedInputValue,
            ...(includedRegionCodes.length > 0 ? { includedRegionCodes } : {}),
            sessionToken: sessionTokenRef.current || undefined,
          });

        if (cancelled) return;
        setSuggestions(nextSuggestions.map((suggestion) => suggestion.placePrediction));
      } catch {
        if (cancelled) return;
        setSuggestions([]);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    void fetchSuggestions();

    return () => {
      cancelled = true;
    };
  }, [countryRestrictions, debouncedInputValue, isLoaded]);

  const handleSelect = async (prediction: GooglePlacePrediction) => {
    const predictionText = prediction.text.toString();
    setInputValue(predictionText);
    setSuggestions([]);
    setLoading(true);

    try {
      const placesLibrary =
        placesLibraryRef.current ||
        (await importGoogleMapsLibrary<GooglePlacesLibrary>('places'));
      placesLibraryRef.current = placesLibrary;

      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      });

      const lat = place.location?.lat?.() ?? null;
      const lng = place.location?.lng?.() ?? null;
      const streetNumber = getAddressComponent(place.addressComponents, 'street_number');
      const route = getAddressComponent(place.addressComponents, 'route');
      const street = streetNumber && route ? `${streetNumber} ${route}` : route;
      const neighborhood =
        getAddressComponent(place.addressComponents, 'sublocality_level_1') ||
        getAddressComponent(place.addressComponents, 'neighborhood');
      const postalCode = getAddressComponent(place.addressComponents, 'postal_code');
      const city =
        getAddressComponent(place.addressComponents, 'locality') ||
        getAddressComponent(place.addressComponents, 'administrative_area_level_2');
      const state = getAddressComponent(place.addressComponents, 'administrative_area_level_1');

      const placeData: PlaceData = {
        address: place.formattedAddress || predictionText,
        lat,
        lng,
        placeId: prediction.placeId,
        street,
        neighborhood,
        postalCode,
        city,
        state,
      };

      onPlaceSelected(placeData);
      trackEvent(posthog, MonitoringEventTypes.AUTOCOMPLETE_PLACE_SELECTED, placeData);
      sessionTokenRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={containerRef} style={{ position: 'relative' }}>
      <TextInput
        {...textInputProps}
        label={label}
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        rightSection={loading ? <Loader size="xs" /> : null}
      />

      {showSuggestions && suggestions.length > 0 ? (
        <Paper
          withBorder
          shadow="md"
          style={{
            position: 'absolute',
            zIndex: 10000000,
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
          }}
        >
          <ScrollArea style={{ maxHeight: 200 }}>
            {suggestions.map((suggestion) => {
              const suggestionText = suggestion.text.toString();

              return (
                <Text
                  key={suggestion.placeId}
                  onClick={() => {
                    void handleSelect(suggestion);
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {suggestionText}
                </Text>
              );
            })}
          </ScrollArea>
        </Paper>
      ) : null}
    </Box>
  );
};

export default AddressAutocomplete;
